(function () {
  'use strict';

  if (window.UnifiedErrors) {
    return;
  }

  const VERSION = '1.0.0';
  const DEBUG_EVENT_NAME = 'unified:debug';

  const STORAGE_KEY = 'ut_error_history';
  const MAX_ENTRIES = 100;

  const SENSITIVE_KEYS = [
    'phone',
    'phone_number',
    'email',
    'email_address',
    'message',
    'form_message',
    'first_name',
    'last_name',
    'name',
    'customer_name',
    'customer_email',
    'customer_phone'
  ];

  let entries = loadEntries();

  function getConfig() {
    return window.UnifiedTrackingConfig || {};
  }

  function isDebugEnabled() {
    return getConfig().debug === true;
  }

  function safeStorageGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function safeStorageSet(key, value) {
    try {
      window.sessionStorage.setItem(
        key,
        value
      );

      return true;
    } catch (error) {
      return false;
    }
  }

  function safeStorageRemove(key) {
    try {
      window.sessionStorage.removeItem(key);

      return true;
    } catch (error) {
      return false;
    }
  }

  function loadEntries() {
    const storedValue = safeStorageGet(
      STORAGE_KEY
    );

    if (!storedValue) {
      return [];
    }

    try {
      const parsed = JSON.parse(storedValue);

      return Array.isArray(parsed)
        ? parsed
        : [];
    } catch (error) {
      return [];
    }
  }

  function saveEntries() {
    safeStorageSet(
      STORAGE_KEY,
      JSON.stringify(entries)
    );
  }

  function sanitizeKey(key) {
    return String(key || '')
      .trim()
      .toLowerCase();
  }

  function isSensitiveKey(key) {
    return SENSITIVE_KEYS.includes(
      sanitizeKey(key)
    );
  }

  function sanitizeValue(value, depth) {
    const currentDepth = depth || 0;

    if (currentDepth > 3) {
      return '[maximum_depth]';
    }

    if (
      value === null ||
      typeof value === 'undefined'
    ) {
      return null;
    }

    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    ) {
      return value;
    }

    if (value instanceof Error) {
      return {
        name: value.name,
        message: value.message
      };
    }

    if (Array.isArray(value)) {
      return value
        .slice(0, 20)
        .map(function (item) {
          return sanitizeValue(
            item,
            currentDepth + 1
          );
        });
    }

    if (typeof value === 'object') {
      const sanitized = {};

      Object.keys(value)
        .slice(0, 30)
        .forEach(function (key) {
          if (isSensitiveKey(key)) {
            return;
          }

          sanitized[key] = sanitizeValue(
            value[key],
            currentDepth + 1
          );
        });

      return sanitized;
    }

    return String(value);
  }

  function sanitizeDetails(details) {
    return sanitizeValue(
      details || {},
      0
    );
  }

  function normalizeSeverity(severity) {
    const normalized = String(
      severity || ''
    ).toLowerCase();

    if (
      normalized === 'fatal' ||
      normalized === 'error' ||
      normalized === 'warning'
    ) {
      return normalized;
    }

    return 'warning';
  }

  function buildFingerprint(
    severity,
    code,
    message
  ) {
    return [
      severity,
      code,
      message
    ].join('|');
  }

  function findExistingEntry(
    severity,
    code,
    message
  ) {
    const fingerprint = buildFingerprint(
      severity,
      code,
      message
    );

    return entries.find(function (entry) {
      return entry.fingerprint === fingerprint;
    });
  }

  function addEntry(options) {
    const data = options || {};

    const severity = normalizeSeverity(
      data.severity
    );

    const code = String(
      data.code || 'unknown_error'
    );

    const message = String(
      data.message || code
    );

    const timestamp =
      data.timestamp ||
      new Date().toISOString();

    const existingEntry = findExistingEntry(
      severity,
      code,
      message
    );

    if (existingEntry) {
      existingEntry.count += 1;
      existingEntry.last_seen = timestamp;
      existingEntry.details = sanitizeDetails(
        data.details
      );

      saveEntries();

      renderConsoleEntry(
        existingEntry,
        true
      );

      return Object.assign(
        {},
        existingEntry
      );
    }

    const entry = {
      id: entries.length + 1,

      severity: severity,
      code: code,
      message: message,

      fingerprint: buildFingerprint(
        severity,
        code,
        message
      ),

      first_seen: timestamp,
      last_seen: timestamp,

      count: 1,

      page_path:
        window.location.pathname,

      details: sanitizeDetails(
        data.details
      )
    };

    entries.push(entry);

    if (entries.length > MAX_ENTRIES) {
      entries.shift();
    }

    saveEntries();
    renderConsoleEntry(entry, false);

    return Object.assign({}, entry);
  }

  function renderConsoleEntry(
    entry,
    repeated
  ) {
    if (!isDebugEnabled()) {
      return;
    }

    const label =
      '[Unified Errors] ' +
      entry.severity.toUpperCase() +
      ' · ' +
      entry.code;

    const data = {
      message: entry.message,
      count: entry.count,
      repeated: repeated,
      page_path: entry.page_path,
      first_seen: entry.first_seen,
      last_seen: entry.last_seen,
      details: entry.details
    };

    if (entry.severity === 'fatal') {
      console.error(label, data);
      return;
    }

    if (entry.severity === 'error') {
      console.error(label, data);
      return;
    }

    console.warn(label, data);
  }

  function getMessageFromDebugData(data) {
    const source = data || {};

    if (source.message) {
      return String(source.message);
    }

    if (
      source.details &&
      source.details.message
    ) {
      return String(
        source.details.message
      );
    }

    return '';
  }

  function getDetailsFromDebugData(data) {
    const source = data || {};

    if (source.details) {
      return source.details;
    }

    return source;
  }

  function classifyDebugEvent(detail) {
    if (!detail || !detail.type) {
      return null;
    }

    const type = detail.type;
    const data = detail.data || {};
    const details =
      getDetailsFromDebugData(data);

    if (type === 'provider_error') {
      return {
        severity: 'error',
        code: 'provider_error',
        message:
          getMessageFromDebugData(data) ||
          'Provider dispatch failed',
        details: details
      };
    }

    if (type === 'consent_error') {
      return {
        severity: 'error',
        code: 'consent_error',
        message:
          getMessageFromDebugData(data) ||
          'Consent check failed',
        details: details
      };
    }

    if (type === 'context_error') {
      return {
        severity: 'error',
        code: 'context_error',
        message:
          getMessageFromDebugData(data) ||
          'Unified Context failed',
        details: details
      };
    }

    if (type === 'provider_unavailable') {
      return {
        severity: 'warning',
        code: 'provider_unavailable',
        message:
          getMessageFromDebugData(data) ||
          'Provider unavailable',
        details: details
      };
    }

    if (type === 'context_unavailable') {
      return {
        severity: 'warning',
        code: 'context_unavailable',
        message:
          getMessageFromDebugData(data) ||
          'Unified Context unavailable',
        details: details
      };
    }

    if (type === 'event_blocked') {
      const reason =
        details.reason ||
        data.reason ||
        '';

      if (
        reason ===
        'analytics_consent_missing'
      ) {
        return null;
      }

      return {
        severity: 'warning',
        code:
          reason ||
          'event_blocked',

        message:
          getMessageFromDebugData(data) ||
          'Event blocked',

        details: details
      };
    }

    return null;
  }

  function handleDebugEvent(event) {
    if (!event.detail) {
      return;
    }

    const classified =
      classifyDebugEvent(event.detail);

    if (!classified) {
      return;
    }

    addEntry({
      severity: classified.severity,
      code: classified.code,
      message: classified.message,
      details: classified.details,
      timestamp:
        event.detail.timestamp
    });
  }

  function report(
    severity,
    code,
    message,
    details
  ) {
    return addEntry({
      severity: severity,
      code: code,
      message: message,
      details: details
    });
  }

  function getAll() {
    return entries.map(function (entry) {
      return Object.assign({}, entry);
    });
  }

  function getErrors() {
    return entries
      .filter(function (entry) {
        return (
          entry.severity === 'error' ||
          entry.severity === 'fatal'
        );
      })
      .map(function (entry) {
        return Object.assign({}, entry);
      });
  }

  function getWarnings() {
    return entries
      .filter(function (entry) {
        return entry.severity === 'warning';
      })
      .map(function (entry) {
        return Object.assign({}, entry);
      });
  }

  function getFatalErrors() {
    return entries
      .filter(function (entry) {
        return entry.severity === 'fatal';
      })
      .map(function (entry) {
        return Object.assign({}, entry);
      });
  }

  function findByCode(code) {
    return entries
      .filter(function (entry) {
        return entry.code === code;
      })
      .map(function (entry) {
        return Object.assign({}, entry);
      });
  }

  function getSummary() {
    const summary = {
      total_unique: entries.length,
      total_occurrences: 0,
      fatal: 0,
      errors: 0,
      warnings: 0,
      last_error: null
    };

    entries.forEach(function (entry) {
      summary.total_occurrences +=
        Number(entry.count) || 0;

      if (entry.severity === 'fatal') {
        summary.fatal += 1;
      }

      if (entry.severity === 'error') {
        summary.errors += 1;
      }

      if (entry.severity === 'warning') {
        summary.warnings += 1;
      }

      summary.last_error = {
        severity: entry.severity,
        code: entry.code,
        message: entry.message,
        last_seen: entry.last_seen
      };
    });

    return summary;
  }

  function printAll() {
    if (!entries.length) {
      console.info(
        '[Unified Errors] No errors or warnings recorded.'
      );

      return [];
    }

    console.table(
      entries.map(function (entry) {
        return {
          id: entry.id,
          severity: entry.severity,
          code: entry.code,
          message: entry.message,
          count: entry.count,
          page_path: entry.page_path,
          last_seen: entry.last_seen
        };
      })
    );

    return getAll();
  }

  function clear() {
    entries = [];

    safeStorageRemove(
      STORAGE_KEY
    );

    console.info(
      '[Unified Errors] Error history cleared.'
    );

    return true;
  }

  window.addEventListener(
    DEBUG_EVENT_NAME,
    handleDebugEvent
  );

  window.UnifiedErrors = {
    version: VERSION,

    report: report,

    getAll: getAll,

    getErrors: getErrors,

    getWarnings: getWarnings,

    getFatalErrors: getFatalErrors,

    findByCode: findByCode,

    getSummary: getSummary,

    printAll: printAll,

    clear: clear
  };

  if (isDebugEnabled()) {
    console.info(
      '[Unified Errors] Error Manager initialized',
      {
        version: VERSION,
        existingEntries: entries.length
      }
    );
  }
})();